/**
 * Copyright © Veeam Software Group GmbH.
 */

import * as React from 'react';
import { Microsoft365InfrastructureObjectType } from '@veeam-vspc/models/web-controllers';

import vboNotConnected from 'images/vbo-not-connected.svg';
import { useIntervalRequest } from 'views/hooks';
import { useLang } from 'views/providers/LangProvider/hooks';
import { TableWidget } from 'views/components/TableWidget';
import backupServer from '../../../../../images/backup-server.svg';
import backupProxy from '../../../../../images/backup-proxy.svg';
import backupRepository from '../../../../../images/backup-repository.svg';
import { Product, AlarmStatus } from '../../enums';
import { getHelpResourceLink } from 'core/utils';
import { useAppServices, useAppStore } from 'views/providers/AppProvider/hooks';
import { NameColumn } from 'views/components/TableWidget/components/NameColumn';
import { NoInfoColumn } from 'views/components/TableWidget/components/NoInfoColumn';
import { HealthyColumn } from 'views/components/TableWidget/components/HealthyColumn';
import { ErrorsColumn } from 'views/components/TableWidget/components/ErrorsColumn';
import { WarningsColumn } from 'views/components/TableWidget/components/WarningsColumn';
import { EmptyColumnPlaceholder } from 'views/components/TableWidget/components/EmptyColumnPlaceholder';
import { TOGGLE_FILTER_ALL_VALUE } from '../../configs';

import type { Microsoft365InfrastructureResponse } from '../../interfaces';

const icons = {
    [Microsoft365InfrastructureObjectType.BackupServer]: backupServer,
    [Microsoft365InfrastructureObjectType.BackupProxy]: backupProxy,
    [Microsoft365InfrastructureObjectType.BackupRepository]: backupRepository,
};

export const VeeamBackupForMicrosoft365: React.FC = () => {
    const lang = useLang();
    const { portalUser } = useAppStore();
    const { enumMapperService } = useAppServices();
    const request = useIntervalRequest<{}, Microsoft365InfrastructureResponse>('Overview/Microsoft365InfrastructureAlarms');
    const items = (request.data as Microsoft365InfrastructureResponse)?.every(item => item.overallCount === 0)
        ? []
        : (request.data as Microsoft365InfrastructureResponse);

    const isResellerUser = portalUser.isServiceProviderGroup();
    const noDataUrl = isResellerUser ? getHelpResourceLink('651') : getHelpResourceLink('653');

    return (
        <TableWidget
            title={lang.VEEAM_BACKUP_FOR_MICROSOFT}
            height={272}
            data={items}
            shiftColumns
            columns={[
                {
                    cellRenderer: ({ rowData, key }) => (
                        <NameColumn
                            icon={icons[rowData.itemType]}
                            name={enumMapperService.getEnumDescription('Microsoft365InfrastructureObjectType', rowData.itemType)}
                            key={key}
                        />
                    ),
                },
                {
                    cellRenderer: ({ rowData, key }) => {
                        if (rowData.overallCount === 0) {
                            return (
                                <React.Fragment key={key}>
                                    <NoInfoColumn />
                                    <EmptyColumnPlaceholder />
                                </React.Fragment>
                            );
                        }

                        if (rowData.errors.value === 0 && rowData.warnings.value === 0) {
                            return (
                                <React.Fragment key={key}>
                                    <HealthyColumn />
                                    <EmptyColumnPlaceholder />
                                </React.Fragment>
                            );
                        }

                        const columns = [];

                        if (rowData.errors.value > 0) {
                            columns.push(<ErrorsColumn count={rowData.errors.value} key='errors' />);
                        }

                        if (rowData.warnings.value > 0) {
                            columns.push(<WarningsColumn count={rowData.warnings.value} key='warnings' />);
                        }

                        while (columns.length < 2) {
                            columns.push(<EmptyColumnPlaceholder key={columns.length} />);
                        }

                        return columns;
                    },
                },
            ]}
            onClick={() => {
                const filters = `statusesFilter/${AlarmStatus.Warning},${AlarmStatus.Error}/productsFilter/${Product.VBM}/` +
                    `dateFilter/${TOGGLE_FILTER_ALL_VALUE}/scopesFilter/${TOGGLE_FILTER_ALL_VALUE}`;

                window.location.assign(`#home/activeAlarms/tab/alarms/${filters}`);
            }}
            loading={request.loading}
            noDataConfig={{
                linkText: lang.START_MANAGING_VEEAM_BACKUP_FOR_MICROSOFT_365,
                icon: vboNotConnected,
                linkHandler: () => window.open(noDataUrl, '_blank', 'noopener, noreferrer'),
            }}
            className='fb-50'
        />
    );
};
